import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { API_BASE_URL as BASE_URL } from '../services/apiBaseUrl';

const authHeaders = (token, tenantId) => ({
  'Authorization': `Bearer ${token}`,
  'X-Tenant-ID': tenantId,
  'Content-Type': 'application/json',
});

const fetchPatients = async (token, tenantId) => {
  const res = await fetch(`${BASE_URL}/patients/my`, {
    headers: authHeaders(token, tenantId),
  });
  if (!res.ok) throw new Error(`${res.status}`);
  const data = await res.json();
  return Array.isArray(data) ? data : (data.content ?? []);
};

const initials = (name = '') =>
  name.split(' ').slice(0, 2).map(w => w[0]?.toUpperCase()).join('') || '?';

const SkeletonRow = () => (
  <div style={{ display: 'flex', gap: 14, padding: '14px 0', borderBottom: '1px solid #f3f4f6', alignItems: 'center' }}>
    <div className="skeleton" style={{ width: 42, height: 42, borderRadius: '50%', flexShrink: 0 }} />
    <div style={{ flex: 1 }}>
      <div className="skeleton skeleton-line" style={{ width: '35%', height: 13, marginBottom: 6 }} />
      <div className="skeleton skeleton-line" style={{ width: '55%', height: 11 }} />
    </div>
    <div className="skeleton skeleton-line" style={{ width: 70, height: 11 }} />
  </div>
);

const PatientRow = ({ patient, idx }) => {
  const name = patient.name || patient.fullName || 'Unknown';
  return (
    <div style={{
      display: 'flex', gap: 14, padding: '14px 0',
      borderBottom: '1px solid #f3f4f6', alignItems: 'center',
      animation: `fadeUp .3s ${idx * 20}ms ease both`,
    }}>
      <div style={{
        width: 42, height: 42, borderRadius: '50%', flexShrink: 0,
        background: 'linear-gradient(135deg, #eef2ff, #ede9fe)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 14, fontWeight: 700, color: '#4f46e5',
        fontFamily: 'Sora, sans-serif',
      }}>
        {initials(name)}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700, fontSize: 13.5, color: '#1e1b4b', marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {name}
        </div>
        <div className="t-meta">
          {patient.phone && <span>📱 {patient.phone}</span>}
          {patient.email && <span style={{ marginLeft: 12 }}>✉️ {patient.email}</span>}
        </div>
      </div>

      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        {patient.age && <div className="t-meta">{patient.age} yrs</div>}
        {patient.gender && <div className="t-meta">{patient.gender === 'M' ? 'Male' : patient.gender === 'F' ? 'Female' : patient.gender}</div>}
      </div>
    </div>
  );
};

const Patients = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { tenant } = useTenant();

  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    if (!token || !tenant?.id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchPatients(token, tenant.id);
      setPatients(data);
    } catch (e) {
      setError(`Could not load patients (${e.message})`);
    } finally {
      setLoading(false);
    }
  }, [token, tenant?.id]);

  useEffect(() => { load(); }, [load]);

  const filtered = patients.filter(p => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    const name = (p.name || p.fullName || '').toLowerCase();
    const phone = (p.phone || '').toLowerCase();
    return name.includes(q) || phone.includes(q);
  });

  return (
    <div className="page">
      <main className="page-main">

        <div className="breadcrumb">
          <button className="t-link" onClick={() => navigate('/')}>Home</button>
          <span>›</span>
          <span className="bc-current">Patients</span>
        </div>

        <div className="fade-up" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: '1.5rem' }}>
          <div>
            <h1 className="t-h1" style={{ marginBottom: 4 }}>🧑‍⚕️ Patients</h1>
            <p className="t-body-sm">
              {!loading && !error && `${patients.length} patient${patients.length !== 1 ? 's' : ''} registered`}
            </p>
          </div>
        </div>

        {/* Search */}
        <div className="card fade-up-1" style={{ marginBottom: '1.25rem', padding: '14px 18px' }}>
          <div className="search-wrap" style={{ marginBottom: 0 }}>
            <svg className="search-icon" width="16" height="16" fill="none" stroke="#9ca3af" strokeWidth="2" viewBox="0 0 24 24">
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35" strokeLinecap="round"/>
            </svg>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="search-input"
              placeholder="Search by name or phone…"
              style={{ height: 38, fontSize: 13 }}
            />
            {search && (
              <button className="search-clear" onClick={() => setSearch('')} style={{ fontSize: 16 }}>×</button>
            )}
          </div>
        </div>

        {error && (
          <div className="error-state" style={{ marginBottom: '1rem' }}>
            <span>⚠️</span> {error}
            <button className="t-link" style={{ marginLeft: 12 }} onClick={load}>Retry</button>
          </div>
        )}

        <div className="card fade-up-2" style={{ padding: 0, overflow: 'hidden' }}>
          {/* Header */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', padding: '10px 20px',
            background: '#f8f9ff', borderBottom: '1px solid #f3f4f6',
            fontSize: 11, fontWeight: 600, color: '#9ca3af',
            textTransform: 'uppercase', letterSpacing: '0.06em',
            fontFamily: 'DM Sans, sans-serif',
          }}>
            <span>Patient</span>
            <span>Details</span>
          </div>

          <div style={{ padding: '0 20px' }}>
            {loading
              ? Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} />)
              : filtered.length === 0
                ? (
                  <div className="empty-state">
                    <div className="empty-icon">🧑‍⚕️</div>
                    <p className="empty-title">
                      {search ? 'No patients match your search' : 'No patients yet'}
                    </p>
                    {search && (
                      <button className="t-link" onClick={() => setSearch('')}>Clear search</button>
                    )}
                  </div>
                )
                : filtered.map((p, i) => <PatientRow key={p.id || i} patient={p} idx={i} />)
            }
          </div>
        </div>

        {!loading && patients.length > 0 && (
          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <span className="t-meta">
              Showing {filtered.length}{filtered.length !== patients.length ? ` of ${patients.length}` : ''} patient{filtered.length !== 1 ? 's' : ''}
            </span>
          </div>
        )}

      </main>
    </div>
  );
};

export default Patients;